import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, ToggleButton, Typography } from '@mui/material';
import Spinner from '../../components/Spinner';
import HudVirtualizedJsonTree from './JsonTree/HudVirtualizedJsonTree';
import { getIn, getTopLevelEntrySizeBytes } from './utils';

const EMPTY_PRIORITY_MAP = {};

const formatSize = (bytes) => (bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(2)} KB`);
const formatValue = (value) => (typeof value === 'string' ? `"${value}"` : String(value));

const DiffHistoryList = ({ entries = [], loading = false, sortBySize = false }) => {
  const [openTick, setOpenTick] = useState(null);
  const [changedOnly, setChangedOnly] = useState(false);
  const sizes = entries.map(({ originalSize }) => originalSize).sort((a, b) => a - b);
  const medianSize = sizes[Math.floor(sizes.length / 2)] || 0;
  const openEntry = entries.find(({ tick }) => tick === openTick);
  const openDiff = openEntry?.diff;
  const { displayDiff, highlightPathSet, changedSubtreePathSet, changeLabelMap } = useMemo(() => {
    const paths = new Set();
    const subtrees = new Set();
    const labels = new Map();
    const displayParts = (path) => {
      const parts = path.split('.');
      const indices = openDiff?.map?.[`${parts[1]}DiffIndices`];
      const displayIndex = Array.isArray(indices) ? indices.indexOf(Number(parts[2])) : -1;
      if (displayIndex >= 0) parts[2] = String(displayIndex);
      return parts;
    };
    const changedPaths = openEntry?.changedPaths?.length
      ? openEntry.changedPaths
      : Object.keys(openDiff?.map || {})
          .filter((key) => !key.endsWith('DiffFull') && !key.endsWith('DiffIndices'))
          .map((key) => `map.${key}`);
    for (const changedPath of changedPaths) {
      const parts = displayParts(changedPath);
      parts.forEach((_, index) => paths.add(parts.slice(0, index + 1).join('.')));
      const value = getIn(openDiff, parts);
      if (value && typeof value === 'object') subtrees.add(parts.join('.'));
    }
    for (const change of openEntry?.changes || []) {
      const path = displayParts(change.path).join('.');
      const previous = Object.prototype.hasOwnProperty.call(change, 'before') ? `was ${formatValue(change.before)}` : change.type;
      labels.set(path, previous);
    }
    const arrayHashes = {};
    for (const [key, hash] of Object.entries(openDiff?.hashMap || {})) {
      const items = openDiff?.map?.[key];
      const indices = openDiff?.map?.[`${key}DiffIndices`];
      const full = openDiff?.map?.[`${key}DiffFull`] === true;
      if (!Array.isArray(items) || (!full && !Array.isArray(indices))) continue;
      arrayHashes[key] = full
        ? { update: 'Full array replacement; individual changed indices unavailable', items }
        : Object.fromEntries(indices.map((index, position) => [`[${index}]`, items[position]]));
      labels.set(`hashMap.${key}`, `hash ${hash}, ${full ? 'full array replacement' : `${indices.length} changed indices`}`);
    }
    const displayDiff = Object.keys(arrayHashes).length ? { ...openDiff, hashMap: { ...openDiff.hashMap, ...arrayHashes } } : openDiff;
    return { displayDiff, highlightPathSet: paths, changedSubtreePathSet: subtrees, changeLabelMap: labels };
  }, [openDiff, openEntry]);
  const priorityMap =
    sortBySize && openDiff?.map && !Array.isArray(openDiff.map)
      ? {
          map: Object.entries(openDiff.map)
            .map(([key, value]) => [key, getTopLevelEntrySizeBytes(key, value)])
            .sort(([, aSize], [, bSize]) => bSize - aSize)
            .map(([key]) => key)
        }
      : EMPTY_PRIORITY_MAP;

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 140,
        maxHeight: 'calc(72vh - 80px)',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgba(0,255,170,0.2)',
        borderRadius: 1,
        padding: 1.25,
        background: 'linear-gradient(160deg, rgba(6,21,29,0.92) 0%, rgba(10,32,40,0.9) 50%, rgba(12,40,46,0.88) 100%)',
        color: '#e8fdfc',
        overflow: 'hidden'
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="subtitle2" sx={{ flexGrow: 1, fontWeight: 600, letterSpacing: 0.4 }}>
          {entries.length ? `Latest ${entries.length} diffs` : 'Latest diffs'}
        </Typography>
        <ToggleButton
          value="changed"
          selected={changedOnly}
          onChange={() => setChangedOnly((current) => !current)}
          size="small"
          sx={{
            textTransform: 'none',
            px: 1.25,
            py: 0.25,
            borderColor: 'rgba(0,255,170,0.3)',
            color: '#d1fffa',
            '&.Mui-selected': { borderColor: '#00ffaa', backgroundColor: 'rgba(0,255,170,0.18)', color: '#ffffff' }
          }}>
          Changed only
        </ToggleButton>
      </Box>
      <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 0.5 }}>
        {loading && (
          <Box role="status" aria-label="Loading latest diffs" sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Spinner inline size={48} />
          </Box>
        )}
        {!loading && entries.length === 0 && (
          <Typography variant="body2" sx={{ color: '#bfece5' }}>
            No diff history is available.
          </Typography>
        )}
        {!loading &&
          entries.map((entry) => {
            const isLarge = entries.length >= 3 && entry.originalSize > medianSize * 10;
            return (
              <Box
                component="details"
                key={entry.tick}
                open={openTick === entry.tick}
                sx={{ mb: 1, border: '1px solid rgba(0,255,170,0.25)', borderRadius: 1, backgroundColor: 'rgba(9,30,38,0.78)', p: 0.75 }}>
                <Typography
                  component="summary"
                  variant="caption"
                  title={isLarge ? 'Large diff: more than 500% above the median' : undefined}
                  onClick={(event) => {
                    event.preventDefault();
                    setOpenTick((current) => (current === entry.tick ? null : entry.tick));
                  }}
                  sx={{ fontWeight: 700, cursor: 'pointer', color: isLarge ? '#ff6b6b' : '#00ffaa' }}>
                  Tick {entry.tick}{' '}
                  <Box component="span" sx={{ color: isLarge ? 'inherit' : 'rgba(255,255,255,0.65)', fontWeight: 400 }}>
                    · Compressed: {formatSize(entry.compressedSize)} Uncompressed: {formatSize(entry.originalSize)}
                  </Box>
                </Typography>
                {openTick === entry.tick && (
                  <Box sx={{ mt: 1, height: 'min(520px, 60vh)' }}>
                    <HudVirtualizedJsonTree
                      data={displayDiff}
                      sizeData={entry.diff}
                      searchTerm=""
                      changeLabelMap={changeLabelMap}
                      highlightPathSet={highlightPathSet}
                      priorityMap={priorityMap}
                      sizePathKey="map"
                      visiblePathSet={changedOnly ? highlightPathSet : undefined}
                      visibleSubtreePathSet={changedOnly ? changedSubtreePathSet : undefined}
                      showAllPropertiesOfMatchingNodes
                    />
                  </Box>
                )}
              </Box>
            );
          })}
      </Box>
    </Box>
  );
};

DiffHistoryList.propTypes = {
  entries: PropTypes.arrayOf(
    PropTypes.shape({
      tick: PropTypes.number.isRequired,
      diff: PropTypes.object.isRequired,
      originalSize: PropTypes.number.isRequired,
      compressedSize: PropTypes.number.isRequired,
      changedPaths: PropTypes.arrayOf(PropTypes.string),
      changes: PropTypes.arrayOf(
        PropTypes.shape({
          path: PropTypes.string.isRequired,
          type: PropTypes.oneOf(['added', 'changed', 'removed']).isRequired,
          before: PropTypes.any
        })
      )
    })
  ),
  loading: PropTypes.bool,
  sortBySize: PropTypes.bool
};

export default DiffHistoryList;
